import React, { useEffect, useMemo, useState } from "react";
import { RootState } from "../../store";
import { ConnectedProps, connect } from "react-redux";
import {
  getPresetList,
  getListMedia,
  getSearchedListMedia,
} from "@/store/media/media.action";
import { formatTimestampToAgo, secondsToHms, useDebounce } from "../../utils";
import { Skeleton } from "..";
import {
  filterAdminPresetsByTeamsAndType,
  mapMediaToPresetPayload,
} from "@/utils/mediautils/CommonFunctions";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import noImage from "../../assets/icons/svg/no-image.svg";
import searchIcon from "../../assets/icons/svg/search.svg";
import { AllAdminPresets } from "@/database";
import { getAdminPresetList } from "@/store/media/media.action";
import { GetMediaApi } from "@/database/media/api";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";
import { PublishedIcon } from "@/components/PublishedIcons/PublishedIcons";

interface RenderCreateMetaPresetProps {
  type?: string;
  handleSelectPreset?: (preset: any) => void;
  adminPresetList?: AllAdminPresets;
  isWizard?: boolean;
}

const MEDIA_LIST_ROWS = 20;

const getPublishedPlatforms = (media: any): string[] => [
  ...new Set<string>(
    (media?.socials ?? [])
      .map((s: any) => s?.job_type)
      .filter((job_type: string | undefined): job_type is string =>
        Boolean(job_type)
      )
  ),
];

const SelectPreset = ({
  type,
  handleSelectPreset,
  isWizard = false,
  presetList,
  getPresetList,
  constUserList,
  isLoading,
  bunchProductList,
  adminPresetList,
  getAdminPresetList,
  mediaList,
  isMediaLoading,
  getListMedia,
  getSearchedListMedia,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectingMediaId, setSelectingMediaId] = useState<string | null>(null);

  useEffect(() => {
    getAdminPresetList({ keys: [type], status: "active" });
  }, [type]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      getSearchedListMedia({
        rows: MEDIA_LIST_ROWS,
        stitle: debouncedSearchTerm,
        key: "secondary",
      });
    } else {
      getListMedia({ rows: MEDIA_LIST_ROWS, key: "secondary" });
    }
  }, [debouncedSearchTerm]);

  const filteredPresetList = useMemo(() => {
    const result = filterAdminPresetsByTeamsAndType(
      bunchProductList,
      adminPresetList || [],
      type
    );
    return Array.isArray(result) ? result : [];
  }, [adminPresetList, bunchProductList]);

  const trackPresetApply = (entityMedia: boolean) => {
    const presetEntity = entityMedia
      ? "media"
      : type === "meta_preset"
        ? "metadata"
        : type === "publish_preset" || type === "social"
          ? "publishing"
          : type || "presets";

    trackEvent("upload", {
      category: "presets",
      entity: presetEntity,
      action: "apply",
      location: isWizard ? "wizard" : "non-wizard",
      ...getTrackUserContext(),
    });
  };

  const handleSelectMedia = async (media: any) => {
    if (!media?.id || selectingMediaId) return;

    setSelectingMediaId(media.id);
    try {
      // Fetch the full media record directly (bypassing redux) so we get
      // resolved contributor/credit info (userContributorData, creditsData,
      // agencydata, agencyCreditData) without touching state.media.mediaDetails
      // - which holds the media currently open for editing on this page.
      const response = await GetMediaApi({ id: media.id });
      const enrichedMedia =
        response?.status === 200 && response.data ? response.data : media;

      trackPresetApply(true);
      // Note: this only stages the media as the previewed "preset" - the
      // source media's socials (carried on the mapped payload) are only
      // committed to state.media.borrowedMediaSocials if/when the user
      // actually clicks Apply on the preview screen (see usePresetManagement's
      // handleNext), not at selection time.
      handleSelectPreset?.(mapMediaToPresetPayload(enrichedMedia));
    } finally {
      setSelectingMediaId(null);
    }
  };

  const handleClearSearch = () => setSearchTerm("");

  return (
    <div className="row h-100" style={{ minHeight: "350px" }}>
      {/* Left pane: search + media list */}
      <div className="col-3 d-flex flex-column h-100">
        <div
          className="fw-medium border-bottom pb-2 flex-shrink-0 d-flex align-items-center"
          style={{ fontSize: "14px", height: "38px", boxSizing: "border-box" }}
        >
          PRESETS
        </div>
        <div className="row align-content-start pb-3 flex-grow-1 border-end overflow-auto">
          {isLoading ? (
            <Skeleton type="table" count={5} />
          ) : filteredPresetList.length > 0 ? (
            filteredPresetList?.map((preset: any, idx: number) => (
              <div
                key={idx}
                className="col-12 pb-0"
                onClick={() => {
                  trackPresetApply(false);
                  handleSelectPreset?.(preset);
                }}>
                <div className="border-bottom px-2 py-3 pointer hover-card">
                  <div
                    className=" fw-bold text-muted text-uppercase mb-2"
                    style={{ fontSize: "13px", color: "#000" }}
                  >
                    {preset?.name || "Untitled Preset"}
                  </div>
                  <div className="text-muted" style={{ fontSize: "11px" }}>
                    Created by:
                    <br />
                    {constUserList?.find(
                      (user: any) => user.id === preset?.ctby
                    )?.name || "Unknown"}
                    , {formatTimestampToAgo(preset?.ct) || "Unknown"}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center">No presets found</div>
          )}
        </div>
      </div>
      <div className="col-9 border-end d-flex flex-column h-100">
        <div className="position-relative flex-shrink-0">
          {/* <label>Search</label> */}
          <img
            src={searchIcon}
            alt=""
            style={{
              position: "absolute",
              top: '18px',
              left: "10px",
              transform: "translateY(-50%)",
              width: "16px",
              height: "16px",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Search media by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: "30px", paddingRight: "40px" }}
          />
          {searchTerm && (
            <button
              type="button"
              className="btn position-absolute"
              style={{
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
              }}
              onClick={handleClearSearch}
            >
              <i className="bi bi-x-circle"></i>
            </button>
          )}
        </div>

        <div className="row gy-3 mt-2 overflow-auto pb-3 flex-grow-1 w-100 align-content-start">
          {isMediaLoading ? (
            <Skeleton type="table" count={4} />
          ) : mediaList && mediaList.length > 0 ? (
            mediaList.map((media: any) => (
              <div
                key={media.id}
                className="col-4"
                style={{
                  cursor: selectingMediaId ? "wait" : "pointer",
                  opacity:
                    selectingMediaId && selectingMediaId !== media.id ? 0.5 : 1,
                }}
                onClick={() => handleSelectMedia(media)}
              >
                <div className="card position-relative h-100 catRow">
                  <div className="position-relative mb-2 ">
                    <div
                      className="rounded ratio ratio-16x9 border bg-secondary"
                      style={{ cursor: "pointer" }}
                    >
                      <img
                        src={
                          (() => {
                            const thumbId =
                              media?.orientation === 2
                                ? media?.images?.portrait ||
                                  media?.images?.thumb
                                : media?.images?.thumb;
                            return thumbId && generateBaseUrl(media)
                              ? getThumbnailUrlUtil({
                                  media,
                                  id: thumbId,
                                  source: media?.source,
                                })
                              : noImage;
                          })()
                        }
                        alt={media.title}
                        className="object-fit-cover position-absolute top-50 start-50 translate-middle rounded-t"
                        style={{ width: "100%", height: "100%" }}
                      />
                      {selectingMediaId === media.id && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50">
                          <span
                            className="spinner-border spinner-border-sm text-light"
                            role="status"
                          ></span>
                        </div>
                      )}
                      <div
                        className="copy-meta-overlay position-absolute top-0 start-0 w-100 h-100 justify-content-center align-items-center"
                        style={{
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                          zIndex: 5,
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm copy-meta-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectMedia(media);
                          }}
                        >
                          Copy Meta
                        </button>
                      </div>
                    </div>
                    <span className="position-absolute bottom-0 end-0 bg-dark text-light px-1 fs-6">
                      {secondsToHms(media.meta?.duration)}
                    </span>
                  </div>
                  <div className="d-flex flex-column px-2 pb-2">
                    <span
                      className="text-truncate mb-1 fw-medium"
                      style={{ fontSize: "13px" }}
                    >
                      {media?.title || "Untitled"}
                    </span>
                    <div className="d-flex align-items-center">
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      <small className="text-grey pe-1">Uploaded on :</small>
                      {formatTimestampToAgo(media?.ct) || "Unknown"}
                    </small>
                    {getPublishedPlatforms(media).length > 0 && (
                      <div className="d-flex gap-1 mt-1 flex-wrap" style={{marginLeft: 'auto'}}>
                        {getPublishedPlatforms(media).map((job_type) => (
                          <PublishedIcon
                            key={job_type}
                            job_type={job_type}
                            status={10}
                            size={14}
                            showQuestionCircle={false}
                          />
                        ))}
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-muted py-5">No media found</div>
          )}
        </div>
      </div>

      {/* Right pane: existing presets */}
    </div>
  );
};

const mapState = (state: RootState) => ({
  presetList: state.media.presetList.list,
  isLoading: state.media.adminPresetList.isLoading,
  constUserList: state.app.constUserList,
  bunchProductList: state.constants.bunchProductList.list,
  adminPresetList: state.media.adminPresetList.list,
  mediaList: state.media.secondaryMediaList.list,
  isMediaLoading: state.media.secondaryMediaList.isLoading,
});

const mapDispatch = {
  getPresetList,
  getAdminPresetList,
  getListMedia,
  getSearchedListMedia,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & RenderCreateMetaPresetProps;

export default connector(SelectPreset);
